import React, { useState, useEffect } from 'react';
import { useI18n } from '../../lib/i18n';
import Modal from '../ui/Modal';
import { Button } from '../ui/Button';
import { createManualCatalogOrder } from '../../lib/store';

interface ManualOrderModalProps {
  open: boolean;
  onClose: () => void;
  onCreated: () => void;
}

interface DraftItem {
  name: string;
  quantity: number;
  price: number;
}

export function ManualOrderModal({ open, onClose, onCreated }: ManualOrderModalProps) {
  const { t } = useI18n();

  const [orderNumber, setOrderNumber] = useState('');
  const [customerName, setCustomerName] = useState('');
  const [customerPhone, setCustomerPhone] = useState('');
  const [shippingAddress, setShippingAddress] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('manual_transfer');
  const [attachmentUrl, setAttachmentUrl] = useState('');
  const [currency, setCurrency] = useState('OMR');
  const [items, setItems] = useState<DraftItem[]>([{ name: '', quantity: 1, price: 0 }]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setOrderNumber(`MAN-${Math.floor(100000 + Math.random() * 900000)}`);
      setCustomerName('');
      setCustomerPhone('');
      setShippingAddress('');
      setPaymentMethod('manual_transfer');
      setAttachmentUrl('');
      setCurrency('OMR');
      setItems([{ name: '', quantity: 1, price: 0 }]);
      setError(null);
    }
  }, [open]);

  const totalAmount = items.reduce((sum, item) => sum + (item.quantity * (item.price || 0)), 0);

  const handleAddItem = () => {
    setItems([...items, { name: '', quantity: 1, price: 0 }]);
  };

  const handleRemoveItem = (idx: number) => {
    if (items.length <= 1) return;
    setItems(items.filter((_, i) => i !== idx));
  };

  const handleItemChange = (idx: number, field: keyof DraftItem, val: any) => {
    const updated = [...items];
    updated[idx] = { ...updated[idx], [field]: val };
    setItems(updated);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!customerName.trim() || !customerPhone.trim() || !shippingAddress.trim()) {
      setError(t('store.orders.requiredFieldsError'));
      return;
    }

    const validItems = items.filter((i) => i.name.trim() && i.quantity > 0);
    if (validItems.length === 0) {
      setError(t('store.orders.atLeastOneItem'));
      return;
    }

    setSubmitting(true);
    setError(null);
    try {
      await createManualCatalogOrder({
        orderNumber: orderNumber.trim(),
        customerName: customerName.trim(),
        customerPhone: customerPhone.trim(),
        shippingAddress: shippingAddress.trim(),
        paymentMethod,
        currency,
        totalAmount,
        manualPaymentAttachmentUrl: attachmentUrl.trim() || null,
        items: validItems.map((i) => ({
          name: i.name.trim(),
          quantity: Number(i.quantity),
          price: Number(i.price),
          currency,
        })),
      });
      onCreated();
      onClose();
    } catch (err: any) {
      setError(err.message || 'Failed to create manual order');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal open={open} onClose={onClose} title={t('store.orders.createManual')}>
      <form onSubmit={handleSubmit} className="space-y-4">
        {error && (
          <div className="rounded-lg bg-danger-50 p-3 text-xs text-danger dark:bg-rose-950/40 dark:text-rose-400">
            {error}
          </div>
        )}

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <div>
            <label className="block text-xs font-semibold text-ink-2 dark:text-slate-300">
              {t('store.orders.orderNumber')} *
            </label>
            <input
              type="text"
              required
              value={orderNumber}
              onChange={(e) => setOrderNumber(e.target.value)}
              className="mt-1 w-full rounded-lg border border-line bg-surface px-3 py-2 text-xs text-ink outline-none focus:border-brand dark:border-slate-700 dark:bg-slate-800 dark:text-white"
            />
          </div>

          <div>
            <label className="block text-xs font-semibold text-ink-2 dark:text-slate-300">
              {t('store.orders.currency')}
            </label>
            <select
              value={currency}
              onChange={(e) => setCurrency(e.target.value)}
              className="mt-1 w-full rounded-lg border border-line bg-surface px-3 py-2 text-xs text-ink outline-none focus:border-brand dark:border-slate-700 dark:bg-slate-800 dark:text-white"
            >
              <option value="OMR">OMR (Omani Rial)</option>
              <option value="AED">AED (UAE Dirham)</option>
              <option value="SAR">SAR (Saudi Riyal)</option>
              <option value="USD">USD (US Dollar)</option>
            </select>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <div>
            <label className="block text-xs font-semibold text-ink-2 dark:text-slate-300">
              {t('store.orders.customerName')} *
            </label>
            <input
              type="text"
              required
              value={customerName}
              onChange={(e) => setCustomerName(e.target.value)}
              placeholder={t('store.customerNamePlaceholder') || 'e.g. Salim Al-Busaidi'}
              className="mt-1 w-full rounded-lg border border-line bg-surface px-3 py-2 text-xs text-ink outline-none focus:border-brand dark:border-slate-700 dark:bg-slate-800 dark:text-white"
            />
          </div>

          <div>
            <label className="block text-xs font-semibold text-ink-2 dark:text-slate-300">
              {t('store.orders.customerPhone')} *
            </label>
            <input
              type="tel"
              required
              value={customerPhone}
              onChange={(e) => setCustomerPhone(e.target.value)}
              placeholder="+96891234567"
              className="mt-1 w-full rounded-lg border border-line bg-surface px-3 py-2 text-xs font-mono text-ink outline-none focus:border-brand dark:border-slate-700 dark:bg-slate-800 dark:text-white"
            />
          </div>
        </div>

        <div>
          <label className="block text-xs font-semibold text-ink-2 dark:text-slate-300">
            {t('store.orders.shippingAddress')} *
          </label>
          <textarea
            rows={2}
            required
            value={shippingAddress}
            onChange={(e) => setShippingAddress(e.target.value)}
            placeholder="Delivery address, city, area"
            className="mt-1 w-full rounded-lg border border-line bg-surface px-3 py-2 text-xs text-ink outline-none focus:border-brand dark:border-slate-700 dark:bg-slate-800 dark:text-white"
          />
        </div>

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <div>
            <label className="block text-xs font-semibold text-ink-2 dark:text-slate-300">
              {t('store.orders.paymentMethod')}
            </label>
            <select
              value={paymentMethod}
              onChange={(e) => setPaymentMethod(e.target.value)}
              className="mt-1 w-full rounded-lg border border-line bg-surface px-3 py-2 text-xs text-ink outline-none focus:border-brand dark:border-slate-700 dark:bg-slate-800 dark:text-white"
            >
              <option value="manual_transfer">{t('store.orders.manualTransfer')}</option>
              <option value="cod">Cash on Delivery (COD)</option>
              <option value="card">Card / Online</option>
            </select>
          </div>

          <div>
            <label className="block text-xs font-semibold text-ink-2 dark:text-slate-300">
              {t('store.orders.receiptUrl')}
            </label>
            <input
              type="url"
              value={attachmentUrl}
              onChange={(e) => setAttachmentUrl(e.target.value)}
              placeholder="https://.../receipt.jpg"
              className="mt-1 w-full rounded-lg border border-line bg-surface px-3 py-2 text-xs text-ink outline-none focus:border-brand dark:border-slate-700 dark:bg-slate-800 dark:text-white"
            />
          </div>
        </div>

        {/* Order Items Builder */}
        <div className="rounded-xl border border-line p-3 dark:border-slate-700">
          <div className="mb-2 flex items-center justify-between">
            <span className="text-xs font-bold text-ink dark:text-white">
              {t('store.orders.items')}
            </span>
            <button
              type="button"
              onClick={handleAddItem}
              className="text-xs font-bold text-brand hover:underline dark:text-brand-tint"
            >
              + {t('store.orders.addItem')}
            </button>
          </div>

          <div className="space-y-2">
            {items.map((item, idx) => (
              <div key={idx} className="flex items-center gap-2">
                <input
                  type="text"
                  required
                  value={item.name}
                  onChange={(e) => handleItemChange(idx, 'name', e.target.value)}
                  placeholder={t('store.orders.itemName')}
                  className="flex-2 rounded-lg border border-line bg-surface px-2.5 py-1.5 text-xs text-ink outline-none focus:border-brand dark:border-slate-700 dark:bg-slate-800 dark:text-white"
                />
                <input
                  type="number"
                  min="1"
                  required
                  value={item.quantity}
                  onChange={(e) => handleItemChange(idx, 'quantity', parseInt(e.target.value, 10) || 1)}
                  placeholder="Qty"
                  className="w-16 rounded-lg border border-line bg-surface px-2 py-1.5 text-xs text-ink outline-none focus:border-brand dark:border-slate-700 dark:bg-slate-800 dark:text-white"
                />
                <input
                  type="number"
                  step="0.1"
                  min="0"
                  required
                  value={item.price}
                  onChange={(e) => handleItemChange(idx, 'price', parseFloat(e.target.value) || 0)}
                  placeholder="Price"
                  className="w-20 rounded-lg border border-line bg-surface px-2 py-1.5 text-xs text-ink outline-none focus:border-brand dark:border-slate-700 dark:bg-slate-800 dark:text-white"
                />
                <button
                  type="button"
                  onClick={() => handleRemoveItem(idx)}
                  disabled={items.length <= 1}
                  className="p-1 text-danger hover:opacity-80 disabled:opacity-30"
                  title="Remove"
                >
                  ✕
                </button>
              </div>
            ))}
          </div>

          <div className="mt-3 flex items-center justify-between border-t border-line pt-2 text-xs font-bold dark:border-slate-700">
            <span className="text-ink-3 dark:text-slate-400">{t('store.orders.totalAmount')}</span>
            <span className="text-sm font-bold text-brand dark:text-brand-tint">
              {totalAmount.toFixed(2)} {currency}
            </span>
          </div>
        </div>

        <div className="flex justify-end gap-2 pt-3 border-t border-line dark:border-slate-700">
          <Button type="button" variant="secondary" onClick={onClose} disabled={submitting}>
            {t('common.cancel')}
          </Button>
          <Button type="submit" variant="primary" loading={submitting}>
            {t('store.orders.createManual')}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
